import React, {SyntheticEvent} from "react";
import "./CompositeElement.scss";
import {
    AnyElementType,
    CompositeElementProps,
    CompositeLayout,
    CompositeNavigation,
} from "@data-force/app/interfaces/store/actions/payload/schema/schemaElements";
import classNames from "classnames";
import {connect} from "react-redux";
import {getTranslate, TranslateFunction} from "react-localize-redux";
import ReactVisibilitySensor from "react-visibility-sensor";
import {SignedResourcesMap} from "../../../interfaces/store/reducers/layoutState";
import StoreState from "../../../interfaces/store/storeState";
import {renewSignedMediaUrl, setNavigationSelection} from "../../../store/actions/layout";
import {RenewSignedMediaUrlAction, SetNavigationSelectionAction} from "../../../interfaces/store/actions/layout";

interface StateProps {
    navigationElements: AnyElementType[]
    signedResourcesMap: SignedResourcesMap
    translate: TranslateFunction
}

interface DispatchProps {
    setNavigationSelection(payload: number): SetNavigationSelectionAction

    renewSignedMediaUrl(payload: string): RenewSignedMediaUrlAction
}

export type Props = CompositeElementProps & StateProps & DispatchProps;

class CompositeElement extends React.Component<Props> {
    constructor(props: Props) {
        super(props);

        this.createCompositeRenderer = this.createCompositeRenderer.bind(this);
        this.handleImageErrorLoading = this.handleImageErrorLoading.bind(this);
    }

    handleImageErrorLoading(event: SyntheticEvent<HTMLImageElement>): void {
        this.props.renewSignedMediaUrl(event.currentTarget.id);
    }

    createCompositeRenderer(): React.ReactElement {
        const {
            children,
            descriptionUrl,
            elementKey,
            fieldName,
            inline,
            label,
            layout,
            signedResourcesMap,
            translate,
        }: Props = this.props;

        const compositeClass: string = classNames({
            "df-chapter": layout === CompositeLayout.CHAPTER,
            "df-questionnaire-block": layout === CompositeLayout.QUESTIONNAIRE_BLOCK,
            "df-questionnaire-item": layout === CompositeLayout.QUESTIONNAIRE_ITEM,
            "df-section": layout === CompositeLayout.SECTION,
            "df-prompt-block": layout === CompositeLayout.PROMPT_BLOCK,
            "df-prompt-header-item": layout === CompositeLayout.PROMPT_HEADER_ITEM,
            "df-prompt-value-item": layout === CompositeLayout.PROMPT_VALUE_ITEM,
            "df-dictation-block": layout === CompositeLayout.DICTATION_BLOCK,
            "df-dictation-header-item": layout === CompositeLayout.DICTATION_HEADER_ITEM,
            "df-dictation-value-item": layout === CompositeLayout.DICTATION_VALUE_ITEM,
        });
        const childrenHolderClass: string = classNames({
            "df-composite-inline": inline,
            "df-space-evenly": layout && [
                CompositeLayout.PROMPT_HEADER_ITEM,
                CompositeLayout.PROMPT_VALUE_ITEM,
                CompositeLayout.DICTATION_HEADER_ITEM,
                CompositeLayout.DICTATION_VALUE_ITEM,
            ].includes(layout),
        });
        const compositeLabel: string = classNames({
            "df-chapter-title": layout === CompositeLayout.CHAPTER,
            "df-block-title": layout && [
                CompositeLayout.QUESTIONNAIRE_BLOCK,
                CompositeLayout.PROMPT_BLOCK,
                CompositeLayout.DICTATION_BLOCK,
            ].includes(layout),
            "df-item-label": layout && [
                CompositeLayout.QUESTIONNAIRE_ITEM,
                CompositeLayout.PROMPT_VALUE_ITEM,
                CompositeLayout.DICTATION_VALUE_ITEM,
            ].includes(layout),
            "df-display-none": !layout || ![
                CompositeLayout.CHAPTER,
                CompositeLayout.QUESTIONNAIRE_BLOCK,
                CompositeLayout.PROMPT_BLOCK,
                CompositeLayout.DICTATION_BLOCK,
                CompositeLayout.QUESTIONNAIRE_ITEM,
                CompositeLayout.PROMPT_VALUE_ITEM,
                CompositeLayout.DICTATION_VALUE_ITEM,
            ].includes(layout),
        });

        // Until signed urls are returned from ss signedResourcesMap will be empty
        const src: string = descriptionUrl ? signedResourcesMap[descriptionUrl] : "";
        const contentPath: string = src ? "contentNotAvailable" : "contentLoading";

        return (
            <div
                className={compositeClass}
                key={`${elementKey}-dictation-composite-item`}
                id={`${compositeClass}-${fieldName}`}
            >
                <div className={compositeLabel}>
                    {label}
                </div>
                {descriptionUrl && (
                    <div className="df-image-container">
                        <img
                            className="df-image-item"
                            src={src}
                            alt={translate(`utils.${contentPath}.text`).toString()}
                            onError={this.handleImageErrorLoading}
                            id={descriptionUrl}
                        />
                    </div>
                )}
                <div className={childrenHolderClass}>
                    {children}
                </div>
            </div>
        );
    }

    updateNavigationSelection(fieldName: string, isVisible: boolean): void {
        const {navigationElements}: Props = this.props;
        if (isVisible) {
            const navigationIndex: number = navigationElements.findIndex(
                (element: AnyElementType) => element.fieldName === fieldName,
            );
            if (navigationIndex !== -1) {
                this.props.setNavigationSelection(navigationIndex);
            }
        }
    }

    render() {
        const {fieldName, layout, navigation}: Props = this.props;
        const compositeRenderer: React.ReactElement = this.createCompositeRenderer();

        return layout === CompositeLayout.CHAPTER && navigation === CompositeNavigation.GLOBAL
            ? (
                <ReactVisibilitySensor
                    partialVisibility
                    minTopValue={650}
                    onChange={this.updateNavigationSelection.bind(this, fieldName)}
                >
                    {compositeRenderer}
                </ReactVisibilitySensor>
            )
            : compositeRenderer;
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        navigationElements: state.layout.content.navigation.elements,
        signedResourcesMap: state.layout.content.signedResourcesMap,
        translate: getTranslate(state.localize),
    };
}

const mapDispatchToProps: DispatchProps = {
    setNavigationSelection,
    renewSignedMediaUrl,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompositeElement);
